import { type ReactNode } from "react";

import { useLocale } from "../lib/i18n.js";
import Anchor from "./Anchor.jsx";
import Callout from "./Callout.jsx";

// Avisos que se repetiam palavra por palavra em dez páginas e três idiomas.
//
// Um Callout copiado é um Callout que envelhece sozinho: a versão em ES ficou
// com um link que a PT já tinha corrigido. Aqui o texto existe uma vez por
// idioma e a página só diz qual aviso quer.
//
// O título de um <Callout> é atributo JSX, e token não resolve em atributo —
// mais uma razão para a string viver no componente, onde é código.

type Strings = { title: string; lead: string; production: string; swapEnv: string; link: string; linkLabel: string };

const SWAP: Record<string, Strings> = {
  pt: {
    title: "Troque pelas suas credenciais antes de ir ao ar",
    lead: "Os valores do sandbox servem para testar, e só.",
    production:
      "Em produção, use o domínio, o `client`, o `agency`, o usuário e a senha que a Infotera entregou para a sua empresa pelo menu de suporte.",
    swapEnv:
      "Trocar de ambiente é trocar `baseUrl` **e** credenciais. Um `401` logo após mudar o domínio quase sempre é credencial de um ambiente sendo usada no outro.",
    link: "/pt/docs/ambientes",
    linkLabel: "Ambientes & limites",
  },
  en: {
    title: "Swap in your own credentials before going live",
    lead: "The sandbox values are for testing, nothing else.",
    production:
      "In production, use the domain, `client`, `agency`, username and password Infotera issued to your company through the support menu.",
    swapEnv:
      "Switching environments means switching `baseUrl` **and** credentials. A `401` right after changing the domain is almost always one environment's credentials used against the other.",
    link: "/en/docs/ambientes",
    linkLabel: "Environments & limits",
  },
  es: {
    title: "Cambia a tus credenciales antes de salir a producción",
    lead: "Los valores del sandbox son para probar, nada más.",
    production:
      "En producción, usa el dominio, el `client`, el `agency`, el usuario y la contraseña que Infotera entregó a tu empresa por el menú de soporte.",
    swapEnv:
      "Cambiar de ambiente es cambiar `baseUrl` **y** credenciales. Un `401` justo después de cambiar el dominio casi siempre es credencial de un ambiente usada en el otro.",
    link: "/es/docs/ambientes",
    linkLabel: "Ambientes y límites",
  },
};

const ENVIRONMENTS: Record<string, { title: string; body: string; link: string; linkLabel: string }> = {
  pt: {
    title: "Ambientes",
    body: "Sandbox e produção são domínios distintos. Integre no sandbox e migre para o seu domínio de produção só depois de passar pelo **Production Readiness Gate**.",
    link: "/pt/docs/ambientes",
    linkLabel: "Ambientes & limites",
  },
  en: {
    title: "Environments",
    body: "Sandbox and production are separate domains. Integrate against the sandbox and move to your production domain only after passing the **Production Readiness Gate**.",
    link: "/en/docs/ambientes",
    linkLabel: "Environments & limits",
  },
  es: {
    title: "Ambientes",
    body: "Sandbox y producción son dominios distintos. Integra en el sandbox y pasa a tu dominio de producción solo después del **Production Readiness Gate**.",
    link: "/es/docs/ambientes",
    linkLabel: "Ambientes y límites",
  },
};

function pick<T>(map: Record<string, T>, locale: string): T {
  return map[locale] ?? (map.pt as T);
}

// Markdown mínimo para as duas marcações que estes textos usam: `code` e
// **negrito**. Trazer um parser inteiro para dois casos seria desproporcional.
function rich(texto: string): ReactNode[] {
  return texto.split(/(`[^`]+`|\*\*[^*]+\*\*)/).map((parte, i) => {
    if (parte.startsWith("`"))
      return (
        <code className="doc-ic" key={i}>
          {parte.slice(1, -1)}
        </code>
      );
    if (parte.startsWith("**")) return <strong key={i}>{parte.slice(2, -2)}</strong>;
    return parte;
  });
}

interface SwapCredentialsProps {
  /** Acrescenta "Veja [Ambientes & limites]" no fim. */
  link?: boolean;
  /**
   * Qual frase vem depois da abertura.
   *
   * "production" (padrão) manda pedir as credenciais ao suporte; "swapEnv" é o
   * caso dos Downloads, onde o que importa é trocar `baseUrl` junto. É uma
   * escolha entre textos traduzidos, e não texto livre, porque a página de
   * Downloads existe só em português: com texto livre, o /en/downloads que cai
   * no fallback mostraria a abertura em inglês e o meio da frase em português.
   */
  body?: "production" | "swapEnv";
}

export function SwapCredentials({ link = false, body = "production" }: SwapCredentialsProps) {
  const { locale } = useLocale();
  const s = pick(SWAP, locale);
  const ver = locale === "en" ? "See " : locale === "es" ? "Ver " : "Veja ";
  return (
    <Callout type="warn" title={s.title}>
      <p className="doc-p">
        {s.lead} {rich(body === "swapEnv" ? s.swapEnv : s.production)}
        {link && (
          <>
            {" "}
            {ver}
            <Anchor href={s.link}>{s.linkLabel}</Anchor>.
          </>
        )}
      </p>
    </Callout>
  );
}

export function EnvironmentsNote() {
  const { locale } = useLocale();
  const s = pick(ENVIRONMENTS, locale);
  const ver = locale === "en" ? "See " : locale === "es" ? "Ver " : "Veja ";
  return (
    <Callout type="note" title={s.title}>
      <p className="doc-p">
        {rich(s.body)} {ver}
        <Anchor href={s.link}>{s.linkLabel}</Anchor>.
      </p>
    </Callout>
  );
}
